<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('saleItemDetail.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          {
            text: $t('saleItemDetail.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <div class="row">
      <div class="col-md-3">
        <div class="form-group">
          <ts-card>
            <ts-card-wrapper>
              <ts-loading-banner :loading="loading">
                <form>
                  <fieldset>
                    <legend>{{ $t('saleItemDetail.criteria') }}</legend>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label class="required">{{
                            $t('saleItemDetail.branch')
                          }}</label>
                          <a-select
                            v-model="model.branch_id"
                            :options="branches"
                            class="ts-w-full"
                          ></a-select>
                          <div class="validate" v-if="errors.has('branch_id')">
                            {{ errors.first('branch_id') }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label class="required">{{
                            $t('saleItemDetail.dateRange')
                          }}</label>
                          <a-range-picker
                            format="DD-MM-YYYY"
                            :defaultValue="rangeDate"
                            :defaultPickerValue="rangeDate"
                            @change="dateRangeChange"
                            :ranges="{
                              Today: [moment(), moment()],
                              'This Month': [
                                moment().startOf('month'),
                                moment().endOf('month')
                              ]
                            }"
                          >
                            <template slot="renderExtraFooter"></template>
                          </a-range-picker>
                          <div class="validate" v-if="errors.has('date_range')">
                            {{ errors.first('date_range') }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label class="required">{{
                            $t('saleItemDetail.location')
                          }}</label>
                          <a-select
                            v-model="model.location_id"
                            :options="locations"
                            class="ts-w-full"
                          ></a-select>
                          <div
                            class="validate"
                            v-if="errors.has('location_id')"
                          >
                            {{ errors.first('location_id') }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-6 ts-text-right">
                        <ts-button
                          outline
                          color="success"
                          class="btn-block"
                          @click.prevent="exportExcel"
                          :waiting="exporting"
                          ><i class="far fa-file-excel"></i>
                          {{ $t('export') }}</ts-button
                        >
                      </div>
                      <div class="col-md-6 ts-text-right">
                        <ts-button
                          color="danger"
                          outline
                          class="btn-block"
                          @click.prevent="preview"
                          :waiting="waiting"
                          ><i class="far fa-file-pdf"></i>
                          {{ $t('preview') }}</ts-button
                        >
                      </div>
                    </div>
                  </fieldset>
                </form>
              </ts-loading-banner>
            </ts-card-wrapper>
          </ts-card>
        </div>
      </div>
      <div class="col-md-9">
        <ts-card>
          <ts-card-wrapper>
            <form>
              <fieldset>
                <legend>{{ $t('saleItemDetail.preview') }}</legend>
                <ts-preview-pdf v-model="waiting" :src="src" />
              </fieldset>
            </form>
          </ts-card-wrapper>
        </ts-card>
      </div>
    </div>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import moment from 'moment'
import { mapActions } from 'vuex'
import * as FileDownload from 'downloadjs'
import { trim } from 'lodash'

export default {
  name: 'saleItemDetail',
  data() {
    return {
      waiting: false,
      exporting: false,
      loading: false,
      errors: new Errors(),
      locations: [],
      branches: [],
      src: '',
      rangeDate: [moment(), moment()],
      model: {
        location_id: null,
        branch_id: null
      }
    }
  },
  created() {
    this.fetchLocation()
    this.model.branch_id = this.$store.state.authUser.default_branch_id
  },
  methods: {
    ...mapActions('reportInventory', [
      'getLocation',
      'saleItemDetail',
      'getBranch'
    ]),
    moment,
    async fetchLocation() {
      this.loading = true
      await this.getLocation({
        fields: {
          setup_inventory_locations: 'location_name,location_id'
        },
        page: -1
      }).then(response => {
        this.locations = response.data.map(el => ({
          value: el.location_id,
          label: el.location_name
        }))
      })
      await this.getBranch()
        .then(response => {
          this.branches = response.data.map(el => ({
            value: el.branch_id,
            label: el.branch_name_en
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    preview() {
      this.errors = new Errors()
      this.waiting = true
      this.src = ''
      this.saleItemDetail(this.model)
        .then(response => {
          this.src = response.url
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    dateRangeChange(dateRange) {
      this.$store.commit(
        'reportInventory/SET_DATE_FROM',
        dateRange[0].format('DD-MM-YYYY')
      )
      this.$store.commit(
        'reportInventory/SET_DATE_TO',
        dateRange[1].format('DD-MM-YYYY')
      )
    },
    exportExcel() {
      this.exporting = true
      this.errors = new Errors()

      this.$store
        .dispatch('reportInventory/saleItemDetailExcel', this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.exporting = false))
    }
  }
}
</script>
